import { makeAutoObservable, observable } from "mobx";
import type { FeElements } from "@project-rouge/rg-three";
import { getFeLayers, getFeVisibility } from "@project-rouge/rg-three";
import deepEqual from "deep-equal";
import type { World } from "./World";
import { BooleanState } from "./BooleanState";
import { NumberState } from "./NumberState";
import { SelectState } from "./SelectState";
import { bbox, polygon, transformScale } from "@turf/turf";
import type mapboxgl from "mapbox-gl";
import type { WmsCapabilities } from "./stencil/wmsCapabilities";
import { parseWmsCapabilities } from "./stencil/wmsCapabilities";
import { computeLockedCorners } from "./stencil/lockCorners";
import type { CatalogEntry } from "./stencil/wmsCatalog";
import { fetchAndParseCapabilities } from "./stencil/wmsCatalog";

type GeoBounds = [sw: [number, number], ne: [number, number]];

export enum ModelVisibilityPreset {
  Abstract = "Abstract",
  Architectural = "Architectural",
  Custom = "Custom",
}
export type FeElementKey = keyof FeElements;

const ModelVisibilityPresets: Record<ModelVisibilityPreset, FeElements | undefined> = {
  [ModelVisibilityPreset.Abstract]: {
    Unit: true,
    Cores: true,
    Corridors: true,
    Services: true,
    Amenities: true,
    Entrances: true,
    Facade: false,
    Roof: false,
    Parapets: false,
    Floors: false,
    Ceilings: false,
    Walls: false,
    Windows: false,
    Doors: false,
    Structure: false,
    Commercials: true,
  },
  [ModelVisibilityPreset.Architectural]: {
    Unit: false,
    Cores: false,
    Corridors: false,
    Services: false,
    Amenities: false,
    Entrances: false,
    Facade: true,
    Roof: true,
    Parapets: true,
    Floors: true,
    Ceilings: true,
    Walls: true,
    Windows: true,
    Doors: true,
    Structure: true,
    Commercials: false,
  },
  [ModelVisibilityPreset.Custom]: undefined,
};

function getPresetFromElements(elements: FeElements): ModelVisibilityPreset {
  for (const [preset, presetElements] of Object.entries(ModelVisibilityPresets)) {
    if (deepEqual(presetElements, elements)) return preset as ModelVisibilityPreset;
  }
  return ModelVisibilityPreset.Custom;
}

export type StencilMode =
  | { kind: "none" }
  | { kind: "image-floating"; imageUrl: string }
  | { kind: "image-locked"; imageUrl: string; coords: [number, number][] }
  | { kind: "wms"; capabilities: WmsCapabilities; selectedLayerName: string };

class StencilState {
  mode: StencilMode = { kind: "none" };
  opacity = new NumberState(50);
  scale = new NumberState(100);
  editMode = false;
  error: string | null = null;
  imgRef: HTMLImageElement | null = null;
  loadedCatalogId: string | null = null;

  constructor() {
    makeAutoObservable(this, { imgRef: observable.ref }, { autoBind: true });
  }

  setImgRef(el: HTMLImageElement | null) {
    this.imgRef = el;
  }

  async setFile(file: File, world: World | null) {
    this.error = null;
    if (file.type === "image/png" || /\.png$/i.test(file.name)) {
      this.disposeImage();
      this.mode = { kind: "image-floating", imageUrl: URL.createObjectURL(file) };
      this.editMode = true;
      this.loadedCatalogId = null;
      return;
    }
    if (/\.(xml|wms)$/i.test(file.name) || file.type.includes("xml")) {
      try {
        const text = await file.text();
        const caps = parseWmsCapabilities(text);
        this.disposeImage();
        this.mode = {
          kind: "wms",
          capabilities: caps,
          selectedLayerName: caps.layers[0].name,
        };
        this.editMode = false;
        this.loadedCatalogId = null;
        world?.addToStencilCatalog({ url: caps.getMapUrl, title: caps.serviceTitle });
      } catch {
        this.error = "stencil.error.invalidWms";
      }
      return;
    }
    this.error = "stencil.error.unsupportedFile";
  }

  lock(map: mapboxgl.Map) {
    if (this.mode.kind !== "image-floating" || !this.imgRef) return;
    const coords = computeLockedCorners(this.imgRef, map);
    this.mode = { kind: "image-locked", imageUrl: this.mode.imageUrl, coords };
  }

  unlock() {
    if (this.mode.kind !== "image-locked") return;
    this.mode = { kind: "image-floating", imageUrl: this.mode.imageUrl };
  }

  selectWmsLayer(name: string) {
    if (this.mode.kind === "wms") this.mode.selectedLayerName = name;
  }

  async setUrl(rawUrl: string, world: World) {
    this.error = null;
    if (!rawUrl.trim()) {
      this.error = "stencil.error.fetchFailed";
      return;
    }
    try {
      const { capabilities, fullUrl } = await fetchAndParseCapabilities(rawUrl);
      this.disposeImage();
      this.mode = {
        kind: "wms",
        capabilities,
        selectedLayerName: capabilities.layers[0].name,
      };
      this.editMode = false;
      this.loadedCatalogId = null;
      world.addToStencilCatalog({ url: fullUrl, title: capabilities.serviceTitle });
    } catch (e) {
      this.error =
        e instanceof TypeError ? "stencil.error.fetchFailed" : "stencil.error.invalidWms";
    }
  }

  async loadCatalogEntry(entry: CatalogEntry) {
    this.error = null;
    try {
      const { capabilities } = await fetchAndParseCapabilities(entry.url);
      this.disposeImage();
      const requested =
        "layerName" in entry && entry.layerName
          ? entry.layerName.split(",").map((s) => s.trim()).filter(Boolean)
          : [];
      const validated = requested.filter((n) =>
        capabilities.layers.some((l) => l.name === n)
      );
      const preselect = validated.length > 0 ? validated.join(",") : undefined;
      this.mode = {
        kind: "wms",
        capabilities,
        selectedLayerName: preselect ?? capabilities.layers[0].name,
      };
      this.editMode = false;
      this.loadedCatalogId = "id" in entry ? entry.id : null;
    } catch {
      this.error = "stencil.error.fetchFailed";
    }
  }

  enterEditMode() {
    if (this.mode.kind !== "image-floating" && this.mode.kind !== "image-locked") return;
    this.editMode = true;
  }
  exitEditMode() {
    this.editMode = false;
  }

  setError(key: string | null) {
    this.error = key;
  }

  clear() {
    this.disposeImage();
    this.mode = { kind: "none" };
    this.error = null;
    this.editMode = false;
    this.loadedCatalogId = null;
  }

  private disposeImage() {
    if (this.mode.kind === "image-floating" || this.mode.kind === "image-locked") {
      URL.revokeObjectURL(this.mode.imageUrl);
    }
    this.scale.set(100);
  }

  get cssOpacity() {
    return this.opacity.value / 100;
  }
  get cssScale() {
    return this.scale.value / 100;
  }
  get hasContent() {
    return this.mode.kind !== "none";
  }
  get isFloating() {
    return this.mode.kind === "image-floating";
  }
  get isLocked() {
    return this.mode.kind === "image-locked";
  }
  get isWms() {
    return this.mode.kind === "wms";
  }
}

class ModelVisibility {
  elements: FeElements;
  preset: ModelVisibilityPreset;
  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.elements = { ...ModelVisibilityPresets.Abstract! };
    this.preset = ModelVisibilityPreset.Abstract;
    // this.visibility = getFeVisibility(elements);
    makeAutoObservable(this, {}, { autoBind: true });
  }
  get visibility() {
    return getFeVisibility(this.elements);
  }
  get layers() {
    return getFeLayers(this.elements);
  }
  get presetList(): ModelVisibilityPreset[] {
    let list = Object.values(ModelVisibilityPreset);
    // we don't let user select "Custom", instead it just gets selected when applicable
    if (this.preset !== ModelVisibilityPreset.Custom) {
      list = list.filter((item) => item !== ModelVisibilityPreset.Custom);
    }
    return list;
  }

  toggle(elem: FeElementKey) {
    const newElements = {
      ...this.elements,
      [elem]: !this.elements[elem],
    };
    this.elements = newElements;
    const newPreset = getPresetFromElements(newElements);
    if (this.preset === newPreset) return;
    this.preset = newPreset;
  }

  updatePreset(newPreset: ModelVisibilityPreset) {
    this.preset = newPreset;
    const newElements = ModelVisibilityPresets[newPreset];
    if (!newElements) return;
    if (deepEqual(this.elements, newElements)) return;
    this.elements = newElements;
  }

  private setElements(elements: FeElements) {
    this.elements = elements;
    this.preset = getPresetFromElements(elements);
  }

  /**
   * Hide every building element (used to capture a basemap + zone-outline
   * "drawing view" for the export). Returns a function that restores the
   * previous visibility.
   */
  hideAll(): () => void {
    const previous = { ...this.elements };
    const allOff = { ...this.elements };
    for (const key of Object.keys(allOff) as FeElementKey[]) allOff[key] = false;
    this.setElements(allOff);
    return () => this.setElements(previous);
  }
}

export enum ToolSelection {
  SELECT = "SELECT",
  CREATE = "CREATE",
  CUT = "CUT",
  SEARCH_PLACE = "SEARCH_PLACE",
  EDGE_OFFSET = "EDGE_OFFSET",
}

const TOOL_SELECTION = [
  { id: ToolSelection.SELECT },
  { id: ToolSelection.CREATE },
  { id: ToolSelection.CUT },
  { id: ToolSelection.SEARCH_PLACE },
  { id: ToolSelection.EDGE_OFFSET },
] as const;

const MAP_STYLES = [
  { id: "light", label: "Light", style: "mapbox://styles/mapbox/light-v11" },
  { id: "streets", label: "Streets", style: "mapbox://styles/mapbox/streets-v12" },
  { id: "satellite", label: "Satellite", style: "mapbox://styles/mapbox/satellite-streets-v12" },
] as const;

export class MapState {
  private mapGl: mapboxgl.Map | null = null;
  private world: World | null = null;
  private stencilPlacementCleanup: (() => void) | null = null;
  floorLevels = new BooleanState(true);
  modelVisibility = new ModelVisibility();
  stencil = new StencilState();
  bearing = new NumberState();
  pitch = new NumberState(90 * (Math.PI / 180));
  selectedTool = new SelectState(TOOL_SELECTION, ToolSelection.SEARCH_PLACE);
  layerSiteContext = new BooleanState();
  viewStyle = new SelectState(MAP_STYLES, "streets");
  layerTopographyLines = new BooleanState();
  layerSiteBoundary = new BooleanState();
  layerZones = new BooleanState();
  viewportCenter: { lng: number; lat: number } | null = null;
  constructor(world: World | null) {
    this.world = world;
    makeAutoObservable<MapState, "mapGl">(this, { mapGl: observable.ref }, { autoBind: true });
  }
  setMapGl(mapGl: mapboxgl.Map | null) {
    this.mapGl = mapGl;
    if (mapGl) {
      const update = () => {
        const c = mapGl.getCenter();
        this.setViewportCenter({ lng: c.lng, lat: c.lat });
      };
      update();
      mapGl.on("moveend", update);
    } else {
      this.setViewportCenter(null);
    }
  }
  setViewportCenter(c: { lng: number; lat: number } | null) {
    this.viewportCenter = c;
  }
  /** Live map bearing in degrees (0 = north up); 0 when the map isn't ready. */
  getMapBearing(): number {
    return this.mapGl?.getBearing() ?? 0;
  }
  /** Live map pitch in degrees (0 = straight-down / top-down). */
  getMapPitch(): number {
    return this.mapGl?.getPitch() ?? 0;
  }
  /** Jump the live camera with no animation — used to force a top-down site-plan shot. */
  setCamera(camera: { pitch?: number; bearing?: number }) {
    this.mapGl?.jumpTo(camera);
  }
  /**
   * Snapshot the live map canvas exactly as shown — basemap, any WMS/B-Plan
   * overlay, and the design — as a PNG data URL for embedding in the export.
   * Relies on `preserveDrawingBuffer` being set on the GL context (see Map.tsx).
   * With `hideBuildings`, the 3D building model is hidden for the shot and zone
   * outlines are injected as temporary Mapbox GeoJSON layers so the plot/zone
   * boundaries always appear clearly on the basemap. Everything is restored
   * afterwards. Returns null if the map isn't ready or the capture fails.
   */
  async captureMapImage(options?: { hideBuildings?: boolean }): Promise<string | null> {
    const map = this.mapGl;
    if (!map) return null;
    const restore = options?.hideBuildings ? this.modelVisibility.hideAll() : null;
    const removeZoneOverlay = options?.hideBuildings ? this.addZoneOverlayLayers(map) : null;
    try {
      // After toggling visibility / adding layers, force a repaint and wait for
      // the map (and the shared Three.js renderer) to settle before reading the
      // preserved drawing buffer.
      if (restore || removeZoneOverlay) await this.waitForMapIdle(map);
      return map.getCanvas().toDataURL("image/png");
    } catch {
      return null;
    } finally {
      removeZoneOverlay?.();
      if (restore) {
        restore();
        map.triggerRepaint();
      }
    }
  }

  /**
   * Temporarily add Mapbox GeoJSON fill + line layers for every zone so zone
   * boundaries are always visible in the site-plan capture, regardless of
   * which Three.js tool is active. Returns a cleanup that removes the layers.
   */
  private addZoneOverlayLayers(map: mapboxgl.Map): (() => void) | null {
    const zones = this.world?.zones;
    if (!zones?.length) return null;

    const sourceId = "__tessa_zone_export__";
    const fillId = `${sourceId}_fill`;
    const lineId = `${sourceId}_line`;

    try {
      const features = zones.map((zone) => {
        const coords = zone.geoOuterRing.map((p) => [p.longitude, p.latitude] as [number, number]);
        if (coords.length > 0) coords.push(coords[0]); // close the ring
        return {
          type: "Feature" as const,
          geometry: { type: "Polygon" as const, coordinates: [coords] },
          properties: { zoneType: zone.zoneType },
        };
      });

      map.addSource(sourceId, {
        type: "geojson",
        data: { type: "FeatureCollection" as const, features },
      });
      map.addLayer({
        id: fillId,
        type: "fill",
        source: sourceId,
        paint: { "fill-color": "#4a90d9", "fill-opacity": 0.12 },
      });
      map.addLayer({
        id: lineId,
        type: "line",
        source: sourceId,
        paint: { "line-color": "#1a5fa8", "line-width": 2.5, "line-opacity": 0.9 },
      });
    } catch {
      return null;
    }

    return () => {
      try {
        if (map.getLayer(lineId)) map.removeLayer(lineId);
        if (map.getLayer(fillId)) map.removeLayer(fillId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch {
        // ignore teardown errors
      }
    };
  }

  private waitForMapIdle(map: mapboxgl.Map): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      map.once("idle", finish);
      map.triggerRepaint();
      // Fallback so the capture never hangs if the map is already idle.
      setTimeout(finish, 400);
    });
  }
  setWorld(world: World | null) {
    this.world = world;
  }
  fitBounds(bbox: [number, number, number, number]) {
    this.mapGl?.fitBounds(bbox, { padding: 0, animate: true, duration: 1000 });
  }
  lockStencil() {
    if (!this.mapGl) return;
    this.stencil.lock(this.mapGl);
  }
  enterStencilPlacement() {
    const map = this.mapGl;
    if (!map) return;
    this.stencilPlacementCleanup?.();
    const previousMaxPitch = map.getMaxPitch();
    map.easeTo({ pitch: 0, duration: 250 });
    map.setMaxPitch(0);
    this.stencilPlacementCleanup = () => {
      map.setMaxPitch(previousMaxPitch);
    };
  }
  exitStencilPlacement() {
    this.stencilPlacementCleanup?.();
    this.stencilPlacementCleanup = null;
  }
  flyTo(cords: { longitude: number; latitude: number }) {
    this.mapGl?.flyTo({
      center: { lng: cords.longitude, lat: cords.latitude },
      zoom: 15,
      padding: 0,
      animate: true,
      duration: 1000,
    });
  }
  get hasWorld() {
    return !!this.world;
  }
  get geoPointOfInterest() {
    return this.world ? { lng: this.world.longitude, lat: this.world.latitude } : null;
  }
  get geoBounds(): GeoBounds {
    return getScaledGeoBounds(this.world);
  }

  get cursor() {
    const mode = this.selectedTool.value.id;
    switch (mode) {
      case ToolSelection.CREATE:
        return "pointer";
      case ToolSelection.CUT:
        return "crosshair";
      default:
        return "default";
    }
  }
}

const UK_BOUNDS = [
  [-8.649357, 49.863344], // southwestern corner of the bounds
  [1.76334, 60.860699], // northeastern corner of the bounds
] as [[number, number], [number, number]];

function getScaledGeoBounds(world: World | null): GeoBounds {
  if (!world?.hasSite) return UK_BOUNDS;
  const projectArea = world.zones.flatMap((zone) => {
    const geoRing = zone.geoOuterRing.map((v) => [v.longitude, v.latitude]);
    return geoRing;
  });

  const poly = polygon([[...projectArea, projectArea[0]]]);
  const offsetPolygon = transformScale(poly, 1.5);
  const offsetBbox = bbox(offsetPolygon);
  const geoBounds: GeoBounds = [
    [offsetBbox[0], offsetBbox[1]], // [minLng, minLat]
    [offsetBbox[2], offsetBbox[3]], // [maxLng, maxLat]
  ];

  return geoBounds;
}
